import { IdName } from 'src/app/shared/models/id-name';
import { Component, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { select, Store } from '@ngxs/store';
import { ToastService } from 'src/app/core/services/toast.service';
import { AttachmentForm, UploaderForm } from '../forms/uploader.form';
import { AttachmentState } from '../state/attachments.state';
import {
  LoadAttachmentCategories,
  LoadAttachmentUnallowedFileTypes,
} from '../state/attachments.actions';

const KB_BYTES = 1024;
const MB_BYTES = KB_BYTES * KB_BYTES;
const MAX_FILE_SIZE_MB = 100;

@Component({
  selector: 'app-uploader-dialog',
  templateUrl: './uploader-dialog.component.html',
  styleUrls: ['./uploader-dialog.component.scss'],
  standalone: false,
})
export class UploaderDialogComponent implements OnInit {
  attachmentCategories = select(AttachmentState.getAttachmentCategories);
  unallowedAttachmentFileTypesSig = select(AttachmentState.getAttachmentUnallowedFileTypes);
  isHovering: boolean;
  form: UploaderForm = new UploaderForm();
  totalFilesSize = 0;
  isFileSizeExceeded = false;
  exceededByMB = 0;
  readonly MAX_FILE_SIZE_MB = MAX_FILE_SIZE_MB;

  constructor(
    public dialogRef: MatDialogRef<UploaderDialogComponent>,
    private store: Store,
    private toastService: ToastService
  ) {}

  ngOnInit(): void {
    this.store.dispatch([new LoadAttachmentUnallowedFileTypes(), new LoadAttachmentCategories()]);
  }

  toggleHover(event: boolean): void {
    this.isHovering = event;
  }

  onFileSelected(event: Event): void {
    this.processFiles((event.target as HTMLInputElement).files as FileList);
  }

  processFiles(fileList: FileList): void {
    let files = Array.from(fileList);

    const emptyFiles = files.filter((file: File) => file.size === 0);
    if (emptyFiles.length > 0) {
      const fileNames = emptyFiles.map((file: File) => file.name).join(', ');
      this.toastService.error('cannotAddEmptyAttachments', { fileNames });
    }

    const unallowedFiles = files.filter((file: File) => this.isUnsupportedFileType(file));
    if (unallowedFiles.length > 0) {
      const fileNames = unallowedFiles.map((file: File) => file.name).join(', ');
      this.toastService.error('cannotAddUnallowedAttachments', { fileNames });
    }

    files = files.filter(
      (file: File) =>
        emptyFiles.every((emptyFile) => file !== emptyFile) &&
        unallowedFiles.every((unallowedFile) => file !== unallowedFile)
    );

    files.forEach((file) =>
      this.form.attachments.push(new AttachmentForm({ name: file.name, categories: [1], file }))
    );
    this.updateTotalSize();
  }

  deleteFile(index: number): void {
    this.form.attachments.removeAt(index);
    this.updateTotalSize();
  }

  upload(): void {
    if (!this.form.valid || this.isFileSizeExceeded) {
      return;
    }

    this.dialogRef.close(this.form.attachmentControls.map((field) => field.value));
  }

  private updateTotalSize(): void {
    this.totalFilesSize = this.form.attachments.controls.reduce(
      (total, control) => total + (control.value.file?.size || 0),
      0
    );

    this.isFileSizeExceeded = this.totalFilesSize > MAX_FILE_SIZE_MB * MB_BYTES;
    if (this.isFileSizeExceeded) {
      this.exceededByMB = Math.ceil((this.totalFilesSize - MAX_FILE_SIZE_MB * MB_BYTES) / MB_BYTES);
    }
  }

  private isUnsupportedFileType(file: File): boolean {
    const types = this.unallowedAttachmentFileTypesSig() ?? [];
    return types.some((fileType: string) => file.name.endsWith(fileType));
  }
}
